import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { clearRememberedShellTab, markShellSessionEnded } from '../core/nav/shellTabRestore';
import { canEnterApp } from '../services/authGate';
import {
  usesFirebaseAuth,
  subscribeToAuthState,
  signOutUser,
  completeGoogleRedirect,
} from '../services/FirebaseAuthService';
import type { AppUser } from '../types';

interface AuthContextValue {
  user: AppUser | null;
  login: (sessionUser: AppUser) => Promise<boolean>;
  logout: () => Promise<void>;
  /** True after the first auth snapshot is applied (always true in mock). */
  authReady: boolean;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
  initialUser = null,
}: {
  children: ReactNode;
  /** Test / storybook seed; production starts null and restores from Firebase. */
  initialUser?: AppUser | null;
}) {
  const [user, setUser] = useState<AppUser | null>(initialUser);
  const [authReady, setAuthReady] = useState(!usesFirebaseAuth() || initialUser != null);

  useEffect(() => {
    // Seeded providers (tests) own the session; skip live Firebase restore.
    if (!usesFirebaseAuth() || initialUser != null) {
      setAuthReady(true);
      return undefined;
    }

    let cancelled = false;
    let settled = false;
    let unsub = () => {};

    const markReady = () => {
      if (cancelled || settled) return;
      settled = true;
      setAuthReady(true);
    };

    void (async () => {
      try {
        const sessionUser = await completeGoogleRedirect();
        if (!cancelled && sessionUser && await canEnterApp(sessionUser)) {
          setUser(sessionUser);
        }
      } catch {
        // Redirect restore is best-effort; first onAuthStateChanged still settles ready.
      }

      if (cancelled) return;

      try {
        // Wait for the first auth snapshot (null or mapped user) before leaving splash.
        unsub = subscribeToAuthState(async (sessionUser: AppUser | null) => {
          if (cancelled) return;
          if (sessionUser && await canEnterApp(sessionUser)) {
            setUser(sessionUser);
          } else {
            setUser(null);
          }
          markReady();
        });
      } catch {
        // Auth subscription unavailable (tests / incomplete Firebase bootstrap).
        markReady();
      }
    })();

    return () => {
      cancelled = true;
      unsub();
    };
  }, [initialUser]);

  const login = useCallback(async (sessionUser: AppUser) => {
    if (!(await canEnterApp(sessionUser))) return false;
    setUser((prev) => {
      // Fresh sign-in only — profile refreshes must not reset the shell tab.
      if (prev == null) clearRememberedShellTab();
      return sessionUser;
    });
    return true;
  }, []);

  const logout = useCallback(async () => {
    markShellSessionEnded();
    if (usesFirebaseAuth()) {
      try {
        await signOutUser();
      } catch {
        // Best-effort sign-out.
      }
    }
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, logout, authReady }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
